import { getUserId } from "src/config/storage";
import type { VintedIntegration } from "src/interfaces/plaidIntegration.interface";
import { uploadFile } from "src/utils/storage";
import { vintedProcessFile } from "src/apiService/modules/vinted";
import type { SyncLock } from "../syncLock";
import { LostSyncLockError } from "../syncLock";
import { NotLoggedError } from "./VintedClient";

export type VintedImportType =
  | "initial-import"
  | "direct-import"
  | "daily-sync";

/**
 * Path id for `GET /api/v2/transactions/{id}` — Vinted `my_orders.transaction_id`.
 * Do not use this for Sales dedup or wallet Sold matching.
 */
export function getVintedOrderApiId(order: unknown): string | null {
  const record = order as Record<string, unknown>;
  const id = record.transaction_id;
  return id == null ? null : String(id);
}

/** UUID / ULID-as-UUID (e.g. `01a0e48d-3814-7480-a689-3a5f7403f8fd`). */
export function isVintedUuidThreadId(id: unknown): boolean {
  if (id == null || id === "") return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(id)
  );
}

/** Numeric conversation id: classic num11 or `500*` 12-digit. */
export function isVintedCanonicalThreadId(id: unknown): boolean {
  if (id == null || id === "") return false;
  const s = String(id);
  if (isVintedUuidThreadId(s)) return false;
  return /^\d{10,}$/.test(s);
}

function addThreadId(ids: string[], value: unknown) {
  if (value == null || value === "") return;
  const s = String(value);
  if (!ids.includes(s)) ids.push(s);
}

type VintedTransactionDetails = {
  transaction?: {
    user_msg_thread_id?: unknown;
    order?: { user_msg_thread_id?: unknown };
  };
};

/**
 * All thread-shaped ids on an order / enrich payload (not the API order id).
 * Keep in sync with backend `collectVintedThreadIds` in `vinted/threadIds.js`.
 */
export function collectVintedThreadIds(
  order: unknown,
  details?: unknown
): string[] {
  const record = order as Record<string, unknown>;
  const nestedDetails = (details ?? record.details) as
    | VintedTransactionDetails
    | undefined;
  const transaction = nestedDetails?.transaction;
  const ids: string[] = [];
  addThreadId(ids, record.conversation_id);
  addThreadId(ids, transaction?.user_msg_thread_id);
  addThreadId(ids, transaction?.order?.user_msg_thread_id);
  return ids;
}

/**
 * MRG `Sales.transaction_id` for a Vinted order.
 *
 * Keep in sync with backend `pickMrgSaleTransactionId` /
 * `mapSales` in `MRGBackendAPI/.../vinted/`. Prefer UUID when enrich exposes
 * both shapes — matches current wallet Sold `entity_id` (fast lookup path).
 */
export function getMrgSaleTransactionId(
  order: unknown,
  details?: unknown
): string | null {
  const threadIds = collectVintedThreadIds(order, details);
  const uuid = threadIds.find(isVintedUuidThreadId);
  if (uuid) return uuid;
  const canonical = threadIds.find(isVintedCanonicalThreadId);
  if (canonical) return canonical;
  if (threadIds.length) return threadIds[0];
  return getVintedOrderApiId(order);
}

/** Add every known thread id for an order into a set (wallet Sold filter). */
export function addVintedThreadIdsToSet(
  target: Set<string>,
  order: unknown,
  details?: unknown
) {
  for (const id of collectVintedThreadIds(order, details)) {
    target.add(id);
  }
  const primary = getMrgSaleTransactionId(order, details);
  if (primary) target.add(primary);
}

/**
 * Wallet Sold line that would stamp / stub a sale. Keep in sync with backend
 * `isVintedSoldInvoiceLine` in `vinted/mappings.js`.
 */
export function isVintedSoldInvoiceLine(line: unknown): boolean {
  const record = line as Record<string, unknown> | null | undefined;
  if (!record || record.pending === true) return false;
  if (String(record.title || "").toLowerCase() !== "sold") return false;
  if (String(record.entity_type || "") !== "user_msg_thread") return false;
  if (record.entity_id == null || record.entity_id === "") return false;
  return true;
}

/**
 * Drop Sold lines whose `entity_id` (or any `mrg_thread_ids` alias) matches a
 * sale already seen/uploaded this sync so the wallet job cannot race a stub
 * ahead of the sales file.
 */
export function filterWalletLinesForKnownSales(
  lines: unknown[],
  knownSaleTransactionIds: Set<string> | undefined
): unknown[] {
  if (!knownSaleTransactionIds?.size) return lines;
  return lines.filter((line) => {
    if (!isVintedSoldInvoiceLine(line)) return true;
    const record = line as Record<string, unknown>;
    const candidates: unknown[] = [record.entity_id];
    if (Array.isArray(record.mrg_thread_ids)) {
      candidates.push(...record.mrg_thread_ids);
    }
    if (record.mrg_canonical_thread_id != null) {
      candidates.push(record.mrg_canonical_thread_id);
    }
    return !candidates.some(
      (id) => id != null && knownSaleTransactionIds.has(String(id))
    );
  });
}

/**
 * Attach `mrg_thread_ids` on Sold lines so the backend can dual-lookup sales
 * keyed under UUID or num11/`500*`. Resolves UUID → `conversation.id` via
 * `GET /api/v2/conversations/{id}` when the enrich map did not already supply
 * the pair.
 *
 * `resolveConversationId` may throw `NotLoggedError` / `LostSyncLockError`;
 * those abort the sync. Other resolve failures leave UUID-only aliases.
 */
export async function attachResolvedThreadIdsToSoldLines(
  lines: unknown[],
  {
    resolveConversationId,
    threadIdByAnyId,
    isAbortError = (e: unknown) =>
      e instanceof NotLoggedError || e instanceof LostSyncLockError,
  }: {
    resolveConversationId: (threadId: string) => Promise<string | null>;
    threadIdByAnyId?: Map<string, string[]>;
    isAbortError?: (e: unknown) => boolean;
  }
): Promise<unknown[]> {
  const resolveCache = new Map<string, string | null>();
  const out: unknown[] = [];
  for (const line of lines) {
    if (!isVintedSoldInvoiceLine(line)) {
      out.push(line);
      continue;
    }
    const record = line as Record<string, unknown>;
    const entityId = String(record.entity_id);
    const threadIds = new Set<string>([entityId]);

    const fromMap = threadIdByAnyId?.get(entityId);
    if (fromMap?.length) {
      for (const id of fromMap) threadIds.add(id);
    } else if (isVintedUuidThreadId(entityId)) {
      let canonical: string | null;
      if (resolveCache.has(entityId)) {
        canonical = resolveCache.get(entityId) ?? null;
      } else {
        try {
          canonical = await resolveConversationId(entityId);
        } catch (e) {
          if (isAbortError(e)) throw e;
          console.warn(
            "[Vinted] Failed to resolve conversation for Sold line",
            {
              entityId,
              error: (e as Error).message,
            }
          );
          canonical = null;
        }
        resolveCache.set(entityId, canonical);
      }
      if (canonical) threadIds.add(canonical);
    }

    out.push({
      ...record,
      mrg_thread_ids: [...threadIds],
    });
  }
  return out;
}

/**
 * Build entity_id → [uuid, canonical, ...] from enriched orders in this sync.
 */
export function buildThreadIdAliasMap(
  orders: unknown[]
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const order of orders) {
    const ids = collectVintedThreadIds(order);
    if (ids.length < 2) continue;
    for (const id of ids) {
      const existing = map.get(id) || [];
      for (const other of ids) {
        if (!existing.includes(other)) existing.push(other);
      }
      map.set(id, existing);
    }
  }
  return map;
}

export function getInventoryListingId(item: unknown): string | null {
  const record = item as Record<string, unknown>;
  const id = record.id || record.item_id || record.listing_id;
  return id == null ? null : String(id);
}

export function getInvoiceLineId(line: unknown): string | null {
  const id = (line as Record<string, unknown>)?.id;
  return id == null ? null : String(id);
}

export async function uploadSalesBatch(
  integration: VintedIntegration,
  orders: unknown[],
  importType: VintedImportType,
  {
    uploadSalesId,
    uploadReturnsId,
    timestamp,
    batchIndex,
  }: {
    uploadSalesId?: string;
    uploadReturnsId?: string;
    timestamp: number;
    batchIndex: number;
  },
  lock: SyncLock
) {
  if (!orders.length) return;

  lock.throwIfAborted();

  const uid = await getUserId();
  const currentFilename = `${timestamp}_${integration.id}_${String(
    batchIndex
  ).padStart(2, "0")}.json`;

  await uploadFile(
    `${uid}/vintedSales/${currentFilename}`,
    JSON.stringify(orders)
  );
  await vintedProcessFile(
    integration.id,
    "sales",
    importType,
    currentFilename,
    { uploadSalesId, uploadReturnsId }
  );
}

export async function uploadTransactionsBatch(
  integration: VintedIntegration,
  lines: unknown[],
  importType: VintedImportType,
  {
    uploadTransactionsId,
    timestamp,
    batchIndex,
  }: {
    uploadTransactionsId?: string;
    timestamp: number;
    batchIndex: number;
  },
  lock: SyncLock
) {
  if (!lines.length) return;

  lock.throwIfAborted();

  const uid = await getUserId();
  const currentFilename = `${timestamp}_${integration.id}_${String(
    batchIndex
  ).padStart(2, "0")}.json`;

  await uploadFile(
    `${uid}/vintedTransactions/${currentFilename}`,
    JSON.stringify(lines)
  );
  await vintedProcessFile(
    integration.id,
    "transactions",
    importType,
    currentFilename,
    { uploadTransactionsId }
  );
}

export async function uploadInventoryBatch(
  integration: VintedIntegration,
  items: unknown[],
  importType: Exclude<VintedImportType, "daily-sync">,
  {
    uploadInventoryId,
    timestamp,
    batchIndex,
  }: {
    uploadInventoryId: string;
    timestamp: number;
    batchIndex: number;
  },
  lock: SyncLock
) {
  if (!items.length) return;

  lock.throwIfAborted();

  const uid = await getUserId();
  const filename = `${timestamp}_${integration.id}_${String(
    batchIndex
  ).padStart(2, "0")}.json`;
  await uploadFile(`${uid}/vintedInventory/${filename}`, JSON.stringify(items));
  await vintedProcessFile(integration.id, "inventory", importType, filename, {
    uploadInventoryId,
  });
}
